import { create } from '@bufbuild/protobuf';
import { timestampFromDate } from '@bufbuild/protobuf/wkt';
import { CalendarDate } from '@internationalized/date';
import { z } from 'zod';

import {
	type MissionListFilter,
	MissionListFilterSchema,
} from '@/gen/mission/v1/mission_pb';

/**
 * URL search params of the case search results screen.
 */
export const caseSearchParamsSchema = z.object({
	firstName: z.string().optional().catch(undefined),
	lastName: z.string().optional().catch(undefined),
	caseNumber: z.string().optional().catch(undefined),
	plateNumber: z.string().optional().catch(undefined),
	phone: z.string().optional().catch(undefined),
	createdAt: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.optional()
		.catch(undefined),
});

export type CaseSearchParams = z.infer<typeof caseSearchParamsSchema>;

export type CaseSearchFormData = {
	firstName: string;
	lastName: string;
	caseNumber: string;
	plateNumber: string;
	phone: string;
	createdAt: CalendarDate | null;
};

export const emptyCaseSearchFormData = (): CaseSearchFormData => ({
	firstName: '',
	lastName: '',
	caseNumber: '',
	plateNumber: '',
	phone: '',
	createdAt: null,
});

const pad = (value: number) => String(value).padStart(2, '0');

const trimmed = (value: string) => {
	const next = value.trim();
	return next === '' ? undefined : next;
};

export const paramsToFormData = (
	params: CaseSearchParams,
): CaseSearchFormData => {
	let createdAt: CalendarDate | null = null;
	if (params.createdAt) {
		const [year, month, day] = params.createdAt.split('-').map(Number);
		createdAt = new CalendarDate(year, month, day);
	}

	return {
		firstName: params.firstName ?? '',
		lastName: params.lastName ?? '',
		caseNumber: params.caseNumber ?? '',
		plateNumber: params.plateNumber ?? '',
		phone: params.phone ?? '',
		createdAt,
	};
};

export const formDataToParams = (
	data: CaseSearchFormData,
): CaseSearchParams => {
	return {
		firstName: trimmed(data.firstName),
		lastName: trimmed(data.lastName),
		caseNumber: trimmed(data.caseNumber),
		plateNumber: trimmed(data.plateNumber),
		phone: trimmed(data.phone),
		createdAt: data.createdAt
			? `${data.createdAt.year}-${pad(data.createdAt.month)}-${pad(data.createdAt.day)}`
			: undefined,
	};
};

export const hasCaseSearchCriteria = (params: CaseSearchParams): boolean =>
	Object.values(params).some((value) => value !== undefined);

export const toMissionListFilter = (
	params: CaseSearchParams,
): MissionListFilter => {
	let createdAfter: Date | undefined;
	let createdBefore: Date | undefined;

	if (params.createdAt) {
		const [year, month, day] = params.createdAt.split('-').map(Number);
		createdAfter = new Date(year, month - 1, day);
		createdBefore = new Date(year, month - 1, day + 1);
	}

	return create(MissionListFilterSchema, {
		beneficiaryFirstName: params.firstName,
		beneficiaryLastName: params.lastName,
		caseNumber: params.caseNumber,
		vehiclePlate: params.plateNumber,
		phone: params.phone,
		createdAfter: createdAfter ? timestampFromDate(createdAfter) : undefined,
		createdBefore: createdBefore ? timestampFromDate(createdBefore) : undefined,
	});
};
